import { useCallback, useEffect, useMemo, type ChangeEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useCurrentOrg } from '@/app/entities/org/model'
import { useRefetchCompanyStatsOnVisit } from '../model/useRefetchCompanyStatsOnVisit'
import {
    CompanyStatsBrandComparisonDashboard,
    CompanyStatsDashboard,
    CompanyStatsDetailedBrandComparisonDashboard,
    CompanyStatsSellerSelect,
} from '@/common/entities/companyStats'
import type { CompanyStatsDataFetchers, CompanyStatsDataHooks } from '@/common/entities/companyStats'
import { getProductBrendsApiV1ProductsOrgIdBrandsGet } from '@/common/api/client/sdk.gen'
import type { PageProductBrandSchema } from '@/common/api/client/types.gen'
import { useSellers } from '@/common/entities/sellers'
import type {
    DetailedComparisonDetailView,
    DetailedComparisonFormat,
} from '@/common/entities/companyStats/lib/detailedBrandComparison'
import { useUrlListFilters } from '@/common/hooks/useUrlListFilters'
import { Select, SelectOption } from '@/common/ui/Form/Select'

export type StatsView = 'stats' | 'compare' | 'detailed'
type StatsCompareBy = 'brand' | 'seller'

const parseCompareBy = (value: string | null): StatsCompareBy => (value === 'seller' ? 'seller' : 'brand')

const parseSelectedBrands = (value: string | null, validIds: Set<string>): string[] => {
    if (!value) return []
    return value
        .split(',')
        .map(id => id.trim())
        .filter(id => validIds.has(id))
}

const parseFormat = (value: string | null): DetailedComparisonFormat => (value === 'count' ? 'count' : 'percent')

const parseDetailView = (value: string | null): DetailedComparisonDetailView =>
    value === 'criteria' ? 'criteria' : 'blocks'

const BRANDS_PAGE_SIZE = 100

const useCompanyStatsBrands = (orgId: string | undefined) =>
    useQuery<PageProductBrandSchema>({
        queryKey: ['company-stats-brands', orgId],
        queryFn: async () => {
            const res = await getProductBrendsApiV1ProductsOrgIdBrandsGet({
                path: { org_id: orgId! },
                query: {
                    size: BRANDS_PAGE_SIZE,
                    order_by: 'name',
                    direction: 'asc',
                },
            })
            return res.data as PageProductBrandSchema
        },
        enabled: Boolean(orgId),
    })

type CompanyStatsPageProps = {
    view: StatsView
    orgId?: string
    orgName?: string
    showOrgName?: boolean
    hooks?: CompanyStatsDataHooks
    fetchers?: CompanyStatsDataFetchers
}

export const CompanyStatsPage = ({
    view,
    orgId,
    orgName,
    showOrgName = true,
    hooks,
    fetchers,
}: CompanyStatsPageProps) => {
    const { data: currentOrg, isLoading: isOrgLoading } = useCurrentOrg()
    const resolvedOrgId = orgId ?? currentOrg?.id
    const resolvedOrgName = orgName ?? currentOrg?.name
    const shouldWaitForCurrentOrg = !orgId && isOrgLoading
    useRefetchCompanyStatsOnVisit(resolvedOrgId)
    const [searchParams, setSearchParams] = useSearchParams()
    const { filterBrand, brandSelectValue, filterSeller, sellerSelectValue } = useUrlListFilters({
        queryParams: { brand: 'brand', seller: 'seller' },
    })
    const { data: brandsPage } = useCompanyStatsBrands(resolvedOrgId)
    const { data: sellersPage } = useSellers(resolvedOrgId)

    const compareBy = parseCompareBy(searchParams.get('compare_by'))
    const brandAId = searchParams.get('brand_a') ?? ''
    const brandBId = searchParams.get('brand_b') ?? ''
    const sellerAId = searchParams.get('seller_a') ?? ''
    const sellerBId = searchParams.get('seller_b') ?? ''

    const brandList = useMemo(() => brandsPage?.items ?? [], [brandsPage?.items])
    const sellerList = useMemo(() => sellersPage?.items ?? [], [sellersPage?.items])
    const brandIdSet = useMemo(() => new Set(brandList.map(brand => brand.id)), [brandList])
    const sellerIdSet = useMemo(() => new Set(sellerList.map(seller => seller.id)), [sellerList])

    const selectedEntityIds = useMemo(() => {
        const paramName = compareBy === 'brand' ? 'brands' : 'sellers'
        const validIds = compareBy === 'brand' ? brandIdSet : sellerIdSet
        return parseSelectedBrands(searchParams.get(paramName), validIds)
    }, [brandIdSet, compareBy, searchParams, sellerIdSet])
    const format = parseFormat(searchParams.get('format'))
    const detailView = parseDetailView(searchParams.get('detail'))

    const brandFilterOptions = useMemo(
        () => [{ value: '', label: 'Все бренды' }, ...brandList.map(brand => ({ value: brand.id, label: brand.name }))],
        [brandList]
    )

    const brandComparisonOptions = useMemo(
        () => brandList.map(brand => ({ id: brand.id, name: brand.name })),
        [brandList]
    )
    const sellerComparisonOptions = useMemo(
        () => sellerList.map(seller => ({ id: seller.id, name: seller.name ?? seller.id })),
        [sellerList]
    )

    useEffect(() => {
        if (view !== 'compare') return

        const options = compareBy === 'brand' ? brandList : sellerList
        const firstParam = compareBy === 'brand' ? 'brand_a' : 'seller_a'
        const secondParam = compareBy === 'brand' ? 'brand_b' : 'seller_b'
        const firstId = compareBy === 'brand' ? brandAId : sellerAId
        const secondId = compareBy === 'brand' ? brandBId : sellerBId

        if (options.length < 2 || (firstId && secondId)) return

        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev)
                if (!firstId) next.set(firstParam, options[0].id)
                if (!secondId) next.set(secondParam, options[1].id)
                return next
            },
            { replace: true }
        )
    }, [brandAId, brandBId, compareBy, sellerAId, sellerBId, sellerList, brandList, setSearchParams, view])

    const handleBrandFilterChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    if (value) next.set('brand', value)
                    else next.delete('brand')
                    next.delete('page')
                    next.delete('scroll')
                    return next
                },
                { replace: true }
            )
        },
        [setSearchParams]
    )

    const handleSellerFilterChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    if (value) next.set('seller', value)
                    else next.delete('seller')
                    next.delete('page')
                    next.delete('scroll')
                    return next
                },
                { replace: true }
            )
        },
        [setSearchParams]
    )

    const handleBrandAChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    next.set('brand_a', value)
                    if (value === next.get('brand_b')) {
                        const fallback = brandList.find(brand => brand.id !== value)
                        if (fallback) next.set('brand_b', fallback.id)
                    }
                    return next
                },
                { replace: true }
            )
        },
        [brandList, setSearchParams]
    )

    const handleBrandBChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    next.set('brand_b', value)
                    if (value === next.get('brand_a')) {
                        const fallback = brandList.find(brand => brand.id !== value)
                        if (fallback) next.set('brand_a', fallback.id)
                    }
                    return next
                },
                { replace: true }
            )
        },
        [brandList, setSearchParams]
    )

    const handleSellerAChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    next.set('seller_a', value)
                    if (value === next.get('seller_b')) {
                        const fallback = sellerList.find(seller => seller.id !== value)
                        if (fallback) next.set('seller_b', fallback.id)
                    }
                    return next
                },
                { replace: true }
            )
        },
        [sellerList, setSearchParams]
    )

    const handleSellerBChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    next.set('seller_b', value)
                    if (value === next.get('seller_a')) {
                        const fallback = sellerList.find(seller => seller.id !== value)
                        if (fallback) next.set('seller_a', fallback.id)
                    }
                    return next
                },
                { replace: true }
            )
        },
        [sellerList, setSearchParams]
    )

    const handleCompareByChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            const value = parseCompareBy(e.target.value)
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    next.set('compare_by', value)
                    next.delete('scroll')

                    if (view === 'compare') {
                        next.delete('brand_a')
                        next.delete('brand_b')
                        next.delete('seller_a')
                        next.delete('seller_b')
                        if (value === 'brand') {
                            if (brandList[0]) next.set('brand_a', brandList[0].id)
                            if (brandList[1]) next.set('brand_b', brandList[1].id)
                        } else {
                            if (sellerList[0]) next.set('seller_a', sellerList[0].id)
                            if (sellerList[1]) next.set('seller_b', sellerList[1].id)
                        }
                    }

                    if (view === 'detailed') {
                        next.delete('brands')
                        next.delete('sellers')
                    }

                    return next
                },
                { replace: true }
            )
        },
        [brandList, sellerList, setSearchParams, view]
    )

    const handleDetailedSelectionChange = useCallback(
        (ids: string[]) => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    const paramName = compareBy === 'brand' ? 'brands' : 'sellers'
                    if (ids.length > 0) next.set(paramName, ids.join(','))
                    else next.delete(paramName)
                    return next
                },
                { replace: true }
            )
        },
        [compareBy, setSearchParams]
    )

    const handleFormatChange = useCallback(
        (nextFormat: DetailedComparisonFormat) => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    next.set('format', nextFormat)
                    return next
                },
                { replace: true }
            )
        },
        [setSearchParams]
    )

    const handleDetailViewChange = useCallback(
        (nextDetailView: DetailedComparisonDetailView) => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    next.set('detail', nextDetailView)
                    return next
                },
                { replace: true }
            )
        },
        [setSearchParams]
    )

    if (shouldWaitForCurrentOrg) {
        return (
            <div className='flex items-center justify-center py-16'>
                <span className='loading loading-spinner loading-lg' aria-label='Загрузка организации' />
            </div>
        )
    }

    if (!resolvedOrgId) {
        return (
            <div className='alert alert-warning'>
                <span>Организация не выбрана</span>
            </div>
        )
    }

    return (
        <div className='flex min-w-0 flex-col gap-6'>
            {view === 'stats' ? (
                <CompanyStatsDashboard
                    orgId={resolvedOrgId}
                    brandId={filterBrand}
                    sellerId={filterSeller}
                    hooks={hooks}
                    header={
                        <div className='flex flex-col gap-4 lg:flex-row justify-between lg:items-center '>
                            <div className='flex-1'>
                                {showOrgName && resolvedOrgName ? <p>Название организации: {resolvedOrgName}</p> : null}
                            </div>
                            <div className='flex flex-wrap items-center justify-end gap-2'>
                                <CompanyStatsSellerSelect
                                    orgId={resolvedOrgId}
                                    value={sellerSelectValue}
                                    onChange={handleSellerFilterChange}
                                />
                                <Select
                                    value={brandSelectValue}
                                    onChange={handleBrandFilterChange}
                                    placeholder='Все бренды'
                                    aria-label='Фильтр по бренду'
                                    size='lg'
                                >
                                    {brandFilterOptions.map(option => (
                                        <SelectOption key={option.value || 'all'} value={option.value}>
                                            {option.label}
                                        </SelectOption>
                                    ))}
                                </Select>
                            </div>
                        </div>
                    }
                />
            ) : view === 'compare' ? (
                <CompanyStatsBrandComparisonDashboard
                    orgId={resolvedOrgId}
                    compareBy={compareBy}
                    entityAId={compareBy === 'brand' ? brandAId : sellerAId}
                    entityBId={compareBy === 'brand' ? brandBId : sellerBId}
                    entityOptions={compareBy === 'brand' ? brandComparisonOptions : sellerComparisonOptions}
                    onCompareByChange={handleCompareByChange}
                    onEntityAChange={compareBy === 'brand' ? handleBrandAChange : handleSellerAChange}
                    onEntityBChange={compareBy === 'brand' ? handleBrandBChange : handleSellerBChange}
                    hooks={
                        hooks
                            ? {
                                  useCompanyStatsCommon: hooks.useCompanyStatsCommon,
                                  useCompanyStatsScores: hooks.useCompanyStatsScores,
                                  useCompanyStatsProducts: hooks.useCompanyStatsProducts,
                              }
                            : undefined
                    }
                />
            ) : compareBy === 'brand' && brandList.length === 0 ? (
                <div>
                    <span>Нет брендов для сравнения</span>
                </div>
            ) : compareBy === 'seller' && sellerList.length === 0 ? (
                <div>
                    <span>Нет продавцов для сравнения</span>
                </div>
            ) : (
                <CompanyStatsDetailedBrandComparisonDashboard
                    orgId={resolvedOrgId}
                    compareBy={compareBy}
                    allEntities={
                        compareBy === 'brand'
                            ? brandList.map(brand => ({ id: brand.id, name: brand.name }))
                            : sellerList.map(seller => ({ id: seller.id, name: seller.name ?? seller.id }))
                    }
                    selectedEntityIds={selectedEntityIds}
                    format={format}
                    detailView={detailView}
                    onCompareByChange={handleCompareByChange}
                    onSelectionChange={handleDetailedSelectionChange}
                    onFormatChange={handleFormatChange}
                    onDetailViewChange={handleDetailViewChange}
                    fetchers={fetchers}
                />
            )}
        </div>
    )
}
